/**
 * Soothing verbs — treat, catnip, stroke, comfort hold, bliss, coax-down.
 *
 * See docs/PLAN.md §4.5 (stroke), §4.11 (coax), treat/catnip pantry.
 */

import type { RunConfig } from './run-config.js';
import type { CatArchetype, CatState } from './types.js';

export interface CatGameplayState {
  restlessness: number;
  /** 0..1+ — saturates at 1; past saturation + hold → coax. */
  bliss: number;
  isPurring: boolean;
  /** Seconds bliss has been ≥ 1 while comfort hold is active. */
  coaxHoldSeconds: number;
  /** Strokes registered while bliss ≥ 1 (stroke cats coax after 2). */
  coaxStrokeCount: number;
  /** Cat is leaving the stack (coaxed); remove from sim next step. */
  leaving: boolean;
  /** Render-only: player is comfort-holding this cat. */
  holdActive: boolean;
  /** Toy escalation — show ? indicator. */
  toyCurious: boolean;
  /** Soothing at half rate while distracted. */
  toyDistracted: boolean;
  /** Seconds since toy was hidden (defuse timer). */
  toyCuriousTimer: number;
}

export function createCatGameplayState(): CatGameplayState {
  return {
    restlessness: 0,
    bliss: 0,
    isPurring: false,
    coaxHoldSeconds: 0,
    coaxStrokeCount: 0,
    leaving: false,
    holdActive: false,
    toyCurious: false,
    toyDistracted: false,
    toyCuriousTimer: 0,
  };
}

export function updatePurringFlag(
  entry: CatGameplayState,
  pressureRatio: number,
  config: RunConfig,
): void {
  if (entry.leaving) {
    entry.isPurring = false;
    return;
  }
  entry.isPurring =
    entry.restlessness < config.purrRestlessnessCap && pressureRatio < config.purrPressureCap;
}

/** Apply calm + bliss from any soothing source. */
export function applySoothing(
  entry: CatGameplayState,
  restlessnessDrop: number,
  blissGain: number,
  distractedMultiplier = 1,
): void {
  if (entry.leaving) return;
  const mult = entry.toyDistracted ? distractedMultiplier : 1;
  entry.restlessness = clamp01(entry.restlessness - restlessnessDrop * mult);
  entry.bliss = Math.max(0, entry.bliss + blissGain * mult);
}

export function applyTreatCalm(
  catPlay: Map<string, CatGameplayState>,
  archetypes: ReadonlyMap<string, CatArchetype>,
  catStates: readonly CatState[],
  targetCatId: string | null,
  amount: number,
  blissPerCalm: number,
): string | null {
  let target = targetCatId;
  if (!target) {
    let max = -1;
    for (const [id, entry] of catPlay) {
      if (entry.leaving) continue;
      if (entry.restlessness > max) {
        max = entry.restlessness;
        target = id;
      }
    }
  }
  if (!target) return null;
  const entry = catPlay.get(target);
  if (!entry || entry.leaving) return null;
  const state = catStates.find((s) => s.catId === target);
  const archetype = state ? archetypes.get(state.archetypeId) : undefined;
  const calm = amount * (archetype?.petAffinity ?? 1);
  applySoothing(entry, calm, calm * blissPerCalm, 1);
  return target;
}

/** Comfort hold for one fixed step. */
export function tickComfortHold(
  entry: CatGameplayState,
  archetype: CatArchetype,
  config: RunConfig,
  dtSeconds: number,
): void {
  if (entry.leaving) return;
  entry.holdActive = true;
  const calm = config.holdCalmPerSecond * archetype.petAffinity * dtSeconds;
  const bliss = config.blissPerCalmUnit * calm;
  const mult = entry.toyDistracted ? config.toyDistractedSoothingMultiplier : 1;
  applySoothing(entry, calm, bliss, mult);
  tickCoaxTimer(entry, config, dtSeconds);
}

export function endComfortHold(entry: CatGameplayState): void {
  entry.holdActive = false;
  if (entry.bliss < 1) {
    entry.coaxHoldSeconds = 0;
    entry.coaxStrokeCount = 0;
  }
}

/** One registered stroke. */
export function applyStroke(
  entry: CatGameplayState,
  archetype: CatArchetype,
  config: RunConfig,
): void {
  if (entry.leaving || archetype.soothingVerb === 'hold') return;
  const calm = config.strokeCalmAmount * archetype.petAffinity;
  const bliss = config.blissPerCalmUnit * calm;
  const mult = entry.toyDistracted ? config.toyDistractedSoothingMultiplier : 1;
  applySoothing(entry, calm, bliss, mult);
  if (entry.bliss >= 1) {
    entry.coaxStrokeCount += 1;
    if (entry.coaxStrokeCount >= 2) {
      entry.leaving = true;
      entry.holdActive = false;
    }
  }
}

/** Catnip — calm every cat in radius; 2× treat per cat. */
export function applyCatnipAt(
  wx: number,
  wy: number,
  states: readonly CatState[],
  archetypes: ReadonlyMap<string, CatArchetype>,
  catPlay: Map<string, CatGameplayState>,
  config: RunConfig,
): number {
  const r = config.catnipRadius;
  const r2 = r * r;
  let count = 0;
  for (const state of states) {
    const archetype = archetypes.get(state.archetypeId);
    if (!archetype) continue;
    const dx = state.x - wx;
    const dy = state.y - wy;
    if (dx * dx + dy * dy > r2) continue;
    const entry = catPlay.get(state.catId);
    if (!entry || entry.leaving) continue;
    const calm = config.treatCalmAmount * config.catnipCalmMultiplier * archetype.petAffinity;
    const mult = entry.toyDistracted ? config.toyDistractedSoothingMultiplier : 1;
    applySoothing(entry, calm, calm * config.blissPerCalmUnit, mult);
    count += 1;
  }
  return count;
}

export function tickCoaxTimer(entry: CatGameplayState, config: RunConfig, dtSeconds: number): void {
  if (entry.bliss >= 1 && entry.holdActive) {
    entry.coaxHoldSeconds += dtSeconds;
    if (entry.coaxHoldSeconds >= config.coaxHoldDurationSeconds) {
      entry.leaving = true;
      entry.holdActive = false;
    }
  } else if (!entry.holdActive) {
    entry.coaxHoldSeconds = 0;
  }
}

export function drainLeavingCats(catPlay: Map<string, CatGameplayState>): string[] {
  const out: string[] = [];
  for (const [id, entry] of catPlay) {
    if (entry.leaving) out.push(id);
  }
  for (const id of out) catPlay.delete(id);
  return out;
}

/** Stroke gesture tracker for one pointer session on a cat. */
export class StrokeTracker {
  private lastStrokeMs = 0;
  private lastSide: -1 | 0 | 1 = 0;
  private distSinceStroke = 0;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private readonly catId: string,
    private readonly catX: number,
    private readonly catY: number,
    private readonly catRotation: number,
    private readonly longAxis: number,
    private readonly config: RunConfig,
  ) {}

  /** @returns true if a stroke was registered this move */
  move(wx: number, wy: number, nowMs: number): boolean {
    const lx = worldToLocalX(wx, wy, this.catX, this.catY, this.catRotation);
    const side: -1 | 1 = lx >= 0 ? 1 : -1;
    const dx = wx - this.lastX;
    const dy = wy - this.lastY;
    this.distSinceStroke += Math.hypot(dx, dy);
    this.lastX = wx;
    this.lastY = wy;

    if (this.lastSide !== 0 && side !== this.lastSide) {
      const minDist = this.longAxis * this.config.minStrokeDistanceRatio;
      const intervalOk = nowMs - this.lastStrokeMs >= this.config.minStrokeIntervalMs;
      if (this.distSinceStroke >= minDist && intervalOk) {
        this.lastStrokeMs = nowMs;
        this.distSinceStroke = 0;
        this.lastSide = side;
        return true;
      }
    }
    if (this.lastSide === 0) this.lastSide = side;
    return false;
  }
}

function worldToLocalX(wx: number, wy: number, cx: number, cy: number, rotation: number): number {
  const dx = wx - cx;
  const dy = wy - cy;
  const cos = Math.cos(-rotation);
  const sin = Math.sin(-rotation);
  return dx * cos - dy * sin;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
